import { CheckCircleFilled } from '@ant-design/icons';
import {
  BackBar,
  cubicMeters,
  describeError,
  dirhams,
  IdTag,
  monthEnd,
  monthLabel,
  monthYear,
  PhotoField,
  toWesternDigits,
} from '@horof/shared';
import { Alert, Button, Input, type InputRef, Result, Skeleton } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { type CombinedError, useClient, useMutation, useQuery } from 'urql';
import { graphql } from './gql';
import {
  type PhotoRequest,
  photoRequestMessage,
  ReadingCheckDocument,
  ReadingPhotoSettingsDocument,
  toPhotoRequest,
} from './reading-photo';
import { nextUnread, type RoundMeter, useRound } from './round';
import { useRoundState } from './round-state';

const SaveReadingDocument = graphql(`
  mutation SaveReading($input: AppSaveConsumptionBillInput!) {
    appSaveConsumptionBill(input: $input) {
      result {
        id
        billMonth
        previousReading
        currentReading
        consumption
        billTotal
      }
    }
  }
`);

type SavedBill = {
  id: string;
  previousReading: number;
  currentReading: number;
  consumption: number | null;
  billTotal: string;
};

export function MeterPage() {
  const { meterId = '' } = useParams();
  const { month } = useRoundState();
  const { meters, loaded, error } = useRound(month);
  const meter = meters.find((m) => m.id === meterId);

  if (error && !loaded) {
    return (
      <div className="page">
        <BackBar label="الجولة" />
        <Alert type="error" showIcon message="تعذّر تحميل العداد" />
      </div>
    );
  }
  if (!loaded) {
    return (
      <div className="page">
        <BackBar label="الجولة" />
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    );
  }
  if (!meter) {
    return (
      <div className="page">
        <BackBar label="الجولة" />
        <Result status="404" title="العداد غير موجود في الجولة" />
      </div>
    );
  }

  // key: a fresh form for each meter when moving to the next one.
  return (
    <MeterReading
      key={meter.id}
      meter={meter}
      total={meters.length}
      next={nextUnread(meters, meter.id)}
    />
  );
}

function MeterReading({
  meter,
  total,
  next,
}: {
  meter: RoundMeter;
  total: number;
  next: RoundMeter | null;
}) {
  const { month } = useRoundState();
  const navigate = useNavigate();
  const inputRef = useRef<InputRef>(null);

  // Already read this month: show the bill; "correct" reopens the input.
  const [saved, setSaved] = useState<SavedBill | null>(meter.bill);
  const [editing, setEditing] = useState(!meter.bill);
  const [value, setValue] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [{ fetching }, saveReading] = useMutation(SaveReadingDocument);
  const client = useClient();
  const [checking, setChecking] = useState(false);
  const [{ data: settings }] = useQuery({
    query: ReadingPhotoSettingsDocument,
  });
  const always = settings?.currentSettings?.readingPhotoAlways === true;
  // arqam's answer for the number typed (readingCheck, or the save's error).
  const [check, setCheck] = useState<{
    reading: number;
    request: PhotoRequest | null;
  } | null>(null);
  const [photoOptional, setPhotoOptional] = useState(false);
  const [photoId, setPhotoId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const reading = value === '' ? null : Number(value);
  // The camera step: arqam's answer for this number, else the committee's
  // switch (known before anything is typed).
  const checked = check && check.reading === reading ? check : null;
  const photoRequest: PhotoRequest | null =
    checked?.request ??
    (always && !checked
      ? { reason: 'ALWAYS', consumption: null, usualConsumption: null }
      : null);
  const photoMissing = photoRequest !== null && !photoId;

  const variablesFor = (n: number) => ({
    meterId: meter.id,
    reading: n,
    period: monthEnd(month),
    // The saved bill's id, so a correction never creates a second bill.
    billId: saved?.id ?? meter.bill?.id ?? null,
  });

  // Ask arqam while the reader types (once they pause), so the camera step
  // shows before «حفظ» rather than after it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: variablesFor reads the same values
  useEffect(() => {
    if (!editing || reading === null || reading < meter.previousReading) {
      return;
    }
    if (check?.reading === reading) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      const result = await client
        .query(ReadingCheckDocument, variablesFor(reading), {
          requestPolicy: 'network-only',
        })
        .toPromise();
      if (cancelled || !result.data) return;
      setCheck({ reading, request: toPhotoRequest(result.data.readingCheck) });
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [editing, reading, meter.previousReading, month, saved?.id]);

  async function save() {
    if (reading === null) {
      setProblem('اكتب الرقم الظاهر على العداد.');
      return;
    }
    if (reading < meter.previousReading) {
      setProblem(
        `الرقم أصغر من القراءة السابقة (${meter.previousReading}). تحقّق من العداد.`,
      );
      return;
    }
    setProblem(null);
    const variables = variablesFor(reading);

    // Not checked yet (saved quickly): ask arqam before saving.
    if (!photoId && !checked) {
      setChecking(true);
      const answer = await client
        .query(ReadingCheckDocument, variables, {
          requestPolicy: 'network-only',
        })
        .toPromise();
      setChecking(false);
      if (answer.error || !answer.data) {
        setProblem(saveErrorMessage(answer.error));
        return;
      }
      const request = toPhotoRequest(answer.data.readingCheck);
      setCheck({ reading, request });
      if (request) return;
    }

    const result = await saveReading({
      input: { ...variables, photoId },
    });
    // The server checks again (e.g. the committee switch changed meanwhile).
    const values = result.error?.graphQLErrors[0]?.extensions ?? {};
    if (values.code === 'READING_PHOTO_REQUIRED') {
      setCheck({ reading, request: toPhotoRequest(values) });
      return;
    }
    if (
      values.code === 'PHOTO_NOT_FOUND' ||
      values.code === 'PHOTO_WRONG_KIND'
    ) {
      setPhotoId(null);
      setCheck({
        reading,
        request: photoRequest ?? {
          reason: 'UNUSUAL',
          consumption: null,
          usualConsumption: null,
        },
      });
      setProblem('تعذّر استعمال الصورة. التقط صورة العداد من جديد.');
      return;
    }
    const bill = result.data?.appSaveConsumptionBill?.result;
    if (result.error || !bill) {
      setProblem(saveErrorMessage(result.error));
      return;
    }
    setSaved(bill);
    setEditing(false);
    setValue('');
    setPhotoId(null);
    setCheck(null);
    setPhotoOptional(false);
  }

  return (
    <div className="page">
      <BackBar
        label="الجولة"
        extra={
          <span className="ltr-num">{`${meter.position} / ${total}`}</span>
        }
      />

      <section className="meter-card">
        <h1 className="meter-title">{meter.memberName || meter.label}</h1>
        <div className="meter-sub">العداد {meter.label}</div>
        <IdTag id={meter.id} label="معرّف العداد" />
        {meter.address && meter.address !== meter.label && (
          <div className="meter-sub">{meter.address}</div>
        )}
      </section>

      <section className="reading-card">
        <div className="field-label">القراءة السابقة</div>
        <div className="big-number ltr-num">{meter.previousReading}</div>
        <div className="field-hint">
          {meter.previousMonth
            ? monthYear(meter.previousMonth)
            : meter.bill
              ? ''
              : 'أول قراءة لهذا العداد'}
        </div>
      </section>

      {meter.lockedByLaterBill ? (
        <Alert
          type="warning"
          showIcon
          message="لا يمكن إدخال قراءة لهذا الشهر"
          description="هذا العداد له فاتورة لشهر لاحق."
        />
      ) : editing ? (
        <section className="reading-card">
          <label className="field-label" htmlFor="reading">
            القراءة الجديدة ({monthLabel(month)})
          </label>
          <Input
            id="reading"
            ref={inputRef}
            className="reading-input ltr-num"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="off"
            enterKeyHint="done"
            value={value}
            status={problem ? 'error' : undefined}
            onChange={(e) => {
              setValue(toWesternDigits(e.target.value).replace(/\D/g, ''));
              setProblem(null);
            }}
            onPressEnter={save}
          />
          {photoRequest && (
            <Alert
              type="warning"
              showIcon
              message={photoRequestMessage(photoRequest)}
            />
          )}
          {(photoRequest || photoOptional) && (
            <PhotoField
              kind="METER_READING"
              label="صورة العداد"
              required={photoRequest !== null}
              hint="صوّر العداد عن قرب بحيث يظهر الرقم بوضوح."
              value={photoId}
              onChange={setPhotoId}
              onBusyChange={setUploading}
            />
          )}
          {problem && <div className="field-error">{problem}</div>}
          <Button
            type="primary"
            size="large"
            block
            loading={fetching || checking}
            disabled={uploading || photoMissing}
            onClick={save}
            className="big-button"
          >
            {photoMissing
              ? 'التقط صورة العداد أولاً'
              : meter.bill
                ? 'حفظ التصحيح'
                : 'حفظ القراءة'}
          </Button>
          {!photoRequest && !photoOptional && (
            <Button type="link" onClick={() => setPhotoOptional(true)}>
              إضافة صورة العداد (اختياري)
            </Button>
          )}
          {saved && (
            <Button
              size="large"
              block
              onClick={() => {
                setEditing(false);
                setProblem(null);
              }}
            >
              إلغاء
            </Button>
          )}
        </section>
      ) : (
        saved && (
          <section className="saved-card">
            <div className="saved-title">
              <CheckCircleFilled /> قراءة {monthLabel(month)} محفوظة
            </div>
            <dl className="saved-values">
              <dt>القراءة</dt>
              <dd className="ltr-num">{saved.currentReading}</dd>
              <dt>الاستهلاك</dt>
              <dd>{cubicMeters(saved.consumption)}</dd>
              <dt>المبلغ</dt>
              <dd>{dirhams(saved.billTotal)}</dd>
            </dl>
            <Button size="large" block onClick={() => setEditing(true)}>
              تصحيح القراءة
            </Button>
          </section>
        )
      )}

      {editing && !meter.lockedByLaterBill ? (
        next && (
          <Button
            type="text"
            size="large"
            block
            onClick={() => navigate(`/meter/${next.id}`, { replace: true })}
          >
            تخطَّ هذا العداد
          </Button>
        )
      ) : next ? (
        <Button
          type="primary"
          size="large"
          block
          className="big-button next-button"
          onClick={() => navigate(`/meter/${next.id}`, { replace: true })}
        >
          العداد التالي: {next.memberName || next.label}
        </Button>
      ) : (
        <Result
          status="success"
          title="انتهت الجولة"
          subTitle="كل العدادات مقروءة هذا الشهر."
          extra={
            <Button size="large" onClick={() => navigate('/')}>
              الرجوع إلى الجولة
            </Button>
          }
        />
      )}
    </div>
  );
}

/** The API's errors, in words the reader can act on. */
const saveErrorMessage = (error: CombinedError | undefined) =>
  describeError(error, 'تعذّر حفظ القراءة. أعد المحاولة.', {
    READING_BELOW_PREVIOUS: ({ previousReading }) =>
      `الرقم أصغر من القراءة السابقة (${previousReading}). تحقّق من العداد.`,
    READING_NEGATIVE: 'اكتب الرقم الظاهر على العداد.',
    READING_BEFORE_LAST_PERIOD: 'هذا العداد له فاتورة لشهر لاحق.',
    NO_BILLING_CONFIGURATION: 'لا توجد أسعار لهذا الشهر. اتصل باللجنة.',
    METER_NOT_FOUND: 'هذا العداد لم يعد موجوداً. ارجع إلى الجولة.',
    BILL_NOT_FOUND: 'الفاتورة المراد تصحيحها لم تعد موجودة. ارجع إلى الجولة.',
  });
